import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ArrowDownToLine, ArrowUpFromLine, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { CashTransaction, TransactionStatus, TransactionType } from '../../types/cash-transaction.types';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';

interface TransactionCardProps {
  transaction: CashTransaction;
  onClick?: () => void;
}

const statusConfig: Record<string, { icon: any; color: string; bg: string; label: string }> = {
  [TransactionStatus.PENDING]: {
    icon: Clock,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50',
    label: 'Pendiente',
  },
  [TransactionStatus.CONFIRMED]: {
    icon: CheckCircle,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    label: 'Confirmada',
  },
  [TransactionStatus.COMPLETED]: {
    icon: CheckCircle,
    color: 'text-green-600',
    bg: 'bg-green-50',
    label: 'Completada',
  },
  [TransactionStatus.CANCELLED]: {
    icon: XCircle,
    color: 'text-gray-500',
    bg: 'bg-gray-100',
    label: 'Cancelada',
  },
  [TransactionStatus.FAILED]: {
    icon: XCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    label: 'Fallida',
  },
  REVERSED: {
    icon: AlertCircle,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    label: 'Revertida',
  },
};

export const TransactionCard = ({ transaction, onClick }: TransactionCardProps) => {
  const navigate = useNavigate();
  const isCashIn = transaction.type === TransactionType.CASH_IN;
  const status = statusConfig[transaction.status] ?? statusConfig[TransactionStatus.PENDING];
  const StatusIcon = status.icon;

  const handleClick = () => {
    if (onClick) onClick();
    else navigate(`/transactions/${transaction.id}`);
  };

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm cursor-pointer hover:shadow-md hover:border-gray-300 transition-all"
    >
      <div className="flex items-center justify-between gap-3">
        {/* Icono tipo */}
        <div
          className={clsx(
            'w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0',
            isCashIn ? 'bg-green-100' : 'bg-red-100'
          )}
        >
          {isCashIn ? (
            <ArrowDownToLine className="text-green-600" size={22} />
          ) : (
            <ArrowUpFromLine className="text-red-600" size={22} />
          )}
        </div>

        {/* Centro: cliente + fecha + referencia */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 truncate">{transaction.customerName}</p>
          <p className="text-xs text-gray-500 truncate">{transaction.customerPhone}</p>
          <p className="text-xs text-gray-400 font-mono truncate mt-0.5">{transaction.reference}</p>
        </div>

        {/* Derecha: monto + estado + fecha */}
        <div className="text-right flex-shrink-0">
          <p className={clsx('text-base font-bold', isCashIn ? 'text-green-600' : 'text-red-600')}>
            {isCashIn ? '+' : '−'}
            {transaction.amount.toLocaleString()} XAF
          </p>
          <div className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs mt-1', status.bg)}>
            <StatusIcon size={11} className={status.color} />
            <span className={status.color}>{status.label}</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {format(new Date(transaction.createdAt), "d MMM, HH:mm", { locale: es })}
          </p>
        </div>
      </div>

      {/* Comisión */}
      {transaction.commissionAmount > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between text-xs text-gray-500">
          <span>Comisión ({transaction.commissionRate}%)</span>
          <span className="font-medium text-orange-600">+{transaction.commissionAmount.toLocaleString()} XAF</span>
        </div>
      )}
    </div>
  );
};

